/**
 * SA-1091: the Inbox's one-click triage filters, built from the mailbox
 * list. Pure.
 *
 * Kind labels are per mailbox policy (`sink_config.kinds`, plus the
 * builtins `internal` / `automated` a policy may relabel under
 * `sink_config.builtin`; `unknown` has the one server label). The list
 * endpoint's `kind=` is global, so one id is one chip across the router
 * mailboxes, in policy order of the first mailbox that declares it: the
 * first label wins and a later mailbox's differing label rides the chip's
 * tooltip. The three builtins come last. A mailbox chip is offered only
 * when two or more router mailboxes exist (one is no choice).
 */
import type { Mailbox } from '@/services/mailboxes'

export interface KindChip {
  id: string
  label: string
  /** The other labels the same id carries on other mailboxes, each with its mailbox's name. */
  alsoKnownAs: { label: string; mailbox: string }[]
}

export interface MailboxChip {
  id: string
  label: string
}

export interface FlagChip {
  id: 'awaiting_approval' | 'needs_triage' | 'handler_failed'
  label: string
}

/** The waiting flags a person acts on, worded once. `handler_failed` is a
 * decided row whose handler failed (the thread's union flag). */
export const FLAG_CHIPS: readonly FlagChip[] = [
  { id: 'awaiting_approval', label: 'Waiting for approval' },
  { id: 'needs_triage', label: 'Needs triage' },
  { id: 'handler_failed', label: 'Failed' },
]

/** The server's `routing_config.BUILTIN_KINDS`, in the order the row shows them. */
export const BUILTIN_KINDS = ['unknown', 'internal', 'automated'] as const
const BUILTIN_LABELS: Record<(typeof BUILTIN_KINDS)[number], string> = {
  unknown: 'Unknown',
  internal: 'Internal',
  automated: 'Automated',
}

export const CLEAR_LABEL = 'Clear'

export function routerMailboxes(mailboxes: readonly Mailbox[]): Mailbox[] {
  return mailboxes.filter((mb) => mb.sink_type === 'router')
}

function labelOf(entry: unknown): string | null {
  if (!entry || typeof entry !== 'object') return null
  const label = (entry as { label?: unknown }).label
  return typeof label === 'string' && label.trim() ? label : null
}

function section(mailbox: Mailbox, key: 'kinds' | 'builtin'): Record<string, unknown> {
  const value = mailbox.sink_config?.[key]
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

export function kindChips(mailboxes: readonly Mailbox[]): KindChip[] {
  const routers = routerMailboxes(mailboxes)
  if (routers.length === 0) return []
  const chips: KindChip[] = []
  const byId = new Map<string, KindChip>()
  const add = (id: string, label: string, mailbox: Mailbox) => {
    const held = byId.get(id)
    if (!held) {
      const chip = { id, label, alsoKnownAs: [] }
      byId.set(id, chip)
      chips.push(chip)
      return
    }
    if (label !== held.label && !held.alsoKnownAs.some((a) => a.label === label)) {
      held.alsoKnownAs.push({ label, mailbox: mailbox.display_name })
    }
  }
  for (const mailbox of routers) {
    for (const [id, entry] of Object.entries(section(mailbox, 'kinds'))) {
      if ((BUILTIN_KINDS as readonly string[]).includes(id)) continue
      add(id, labelOf(entry) ?? id, mailbox)
    }
  }
  const builtins: KindChip[] = []
  const builtinById = new Map<string, KindChip>()
  for (const id of BUILTIN_KINDS) {
    const chip: KindChip = { id, label: BUILTIN_LABELS[id], alsoKnownAs: [] }
    builtinById.set(id, chip)
    builtins.push(chip)
  }
  // A policy's builtin relabel: the first mailbox's word wins, as above.
  const relabeled = new Set<string>()
  for (const mailbox of routers) {
    for (const [id, entry] of Object.entries(section(mailbox, 'builtin'))) {
      const chip = builtinById.get(id)
      const label = labelOf(entry)
      if (!chip || !label) continue
      if (!relabeled.has(id)) {
        chip.label = label
        relabeled.add(id)
      } else if (label !== chip.label && !chip.alsoKnownAs.some((a) => a.label === label)) {
        chip.alsoKnownAs.push({ label, mailbox: mailbox.display_name })
      }
    }
  }
  return [...chips, ...builtins]
}

export function mailboxChips(mailboxes: readonly Mailbox[]): MailboxChip[] {
  const routers = routerMailboxes(mailboxes)
  if (routers.length < 2) return []
  return routers.map((mb) => ({ id: mb.id, label: mb.display_name }))
}

/** The tooltip on a chip whose id carries other labels elsewhere. */
export function alsoKnownAsText(chip: KindChip): string | null {
  if (chip.alsoKnownAs.length === 0) return null
  return 'Also ' + chip.alsoKnownAs.map((a) => `“${a.label}” on ${a.mailbox}`).join(', ')
}

/** SA-1127: the Filters menu's words, and the toolbar's chips for the filters that are on. */
export const FILTERS_LABEL = 'Filters'
export const CLEAR_ALL_LABEL = 'Clear all'
export const ACTIVE_FILTERS_LABEL = 'Active filters'
export const GROUP_LABELS = { kind: 'Kind', flag: 'Waiting', mailbox: 'Mailbox' } as const
export type FilterGroup = keyof typeof GROUP_LABELS

/** The trigger's accessible name, the register toolbar's words. */
export function filtersName(active: number): string {
  return active > 0 ? `Filters, ${active} active` : FILTERS_LABEL
}

export function activeChipText(group: FilterGroup, label: string): string {
  return `${GROUP_LABELS[group]}: ${label}`
}

export function removeChipName(group: FilterGroup, label: string): string {
  return `Remove ${activeChipText(group, label)}`
}

export interface ActiveFilter {
  group: FilterGroup
  id: string
  label: string
}

/** The selections, in group order, each with the label its chip shows; an
 * id no policy declares any more reads as itself, so it can still be cleared. */
export function activeFilters(
  mailboxes: readonly Mailbox[],
  sel: { kind: string | null; flag: string | null; mailbox: string | null },
): ActiveFilter[] {
  const out: ActiveFilter[] = []
  if (sel.kind) {
    const label = kindChips(mailboxes).find((chip) => chip.id === sel.kind)?.label ?? sel.kind
    out.push({ group: 'kind', id: sel.kind, label })
  }
  if (sel.flag) {
    const label = FLAG_CHIPS.find((chip) => chip.id === sel.flag)?.label ?? sel.flag
    out.push({ group: 'flag', id: sel.flag, label })
  }
  if (sel.mailbox) {
    const label = mailboxes.find((mb) => mb.id === sel.mailbox)?.display_name ?? sel.mailbox
    out.push({ group: 'mailbox', id: sel.mailbox, label })
  }
  return out
}
